import { ApiStyleCriteriaListTaskTypeEnum, StyleCriterion } from '@api';
import { Button, CheckboxGroup, CircleLoader, FormError, FormRow, Icon, Modal, Tooltip } from '@components/ui';
import { depaginate, useApi } from '@data-access';
import { ErrorHelper } from '@helpers';
import { getStyleScaleLabel } from '@lib/styleMetric';
import { questionnaireStore } from '@stores';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import stepStyles from '../../Steps.module.scss';

import { StyleCriterionForm } from './components/StyleCriterionForm';
import styles from './StyleStep.module.scss';

type StyleStepData = {
  styleCriteria: number[];
};

export const StyleStep = observer(() => {
  const { api } = useApi();
  const { taskType } = questionnaireStore.questionnaireData;

  const [criteria, setCriteria] = useState<StyleCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [criterionToEdit, setCriterionToEdit] = useState<StyleCriterion | null>(null);
  const [criterionToDelete, setCriterionToDelete] = useState<StyleCriterion | null>(null);

  const {
    watch,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<StyleStepData>();

  const styleCriteria = watch('styleCriteria') ?? [];

  const fetchCriteria = useCallback(async () => {
    try {
      // ApiStyleCriteriaListTaskTypeEnum is generated separately but has the same values as TaskTypeEnum
      const result = await depaginate(page =>
        api.apiStyleCriteriaList({ page, taskType: taskType as ApiStyleCriteriaListTaskTypeEnum }),
      );
      setCriteria(result);
    } catch (err) {
      ErrorHelper.handleErrorNotification(err);
    } finally {
      setLoading(false);
    }
  }, [api, taskType]);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  const setSelection = (ids: number[]) => {
    setValue('styleCriteria', ids);
    if (ids.length > 0) clearErrors('styleCriteria');
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setCriterionToEdit(null);
  };

  const handleSaved = (criterion: StyleCriterion, isNew: boolean) => {
    closeFormModal();
    if (isNew) setSelection([...(getValues('styleCriteria') ?? []), criterion.id]);
    fetchCriteria();
  };

  const handleDelete = async () => {
    const { id } = criterionToDelete;
    setCriterionToDelete(null);
    try {
      await api.apiStyleCriteriaDestroy({ id });
      setValue(
        'styleCriteria',
        (getValues('styleCriteria') ?? []).filter(selectedId => selectedId !== id),
      );
      toast.success('Successfully deleted style criterion', { autoClose: 6000 });
      fetchCriteria();
    } catch (err) {
      ErrorHelper.handleErrorNotification(err);
    }
  };

  // Icons sit inside the checkbox <label>; preventDefault stops the click from toggling the checkbox
  const onIconClick = (action: () => void) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    action();
  };

  const options = criteria.map(criterion => ({
    id: criterion.id,
    name: (
      <div className={styles.criterion}>
        <div className={styles.criterionInfo}>
          <span>{criterion.name}</span>
          <span className={styles.scale}>{getStyleScaleLabel(criterion.scale)}</span>
          <Tooltip
            content={
              <div>
                <div className={stepStyles.criterionTitle}>Definition:</div>
                <div className={stepStyles.criterionDefinitionContent}>{criterion.definition}</div>
                <div className={stepStyles.criterionTitle}>Example:</div>
                <div className={stepStyles.criterionDefinitionContent}>{criterion.example}</div>
              </div>
            }
            placement="top"
            isFixed
          >
            <Icon name="question-mark" />
          </Tooltip>
        </div>
        <div className={styles.actions}>
          <Icon
            name="edit"
            title="Edit style criterion"
            onClick={onIconClick(() => {
              setCriterionToEdit(criterion);
              setShowFormModal(true);
            })}
          />
          <Icon
            name="delete"
            title="Delete style criterion"
            onClick={onIconClick(() => setCriterionToDelete(criterion))}
          />
        </div>
      </div>
    ),
  }));

  return (
    <div>
      <ol className={stepStyles.list}>
        <div className={styles.wrapper}>
          <FormRow id="styleCriteria">
            <li className={stepStyles.questionLabel}>
              <div>Style criteria</div>
            </li>
            <p className={styles.helpText}>
              An LLM judge scores each output against each selected criterion, from 0 (fully follows the style) to 1
              (clearly violates it).
            </p>
            {loading ? (
              <CircleLoader className={stepStyles.loader} />
            ) : (
              <div className={stepStyles.reportTypeSelectWrapper}>
                <div className={stepStyles.checkboxGroupWrapper}>
                  {criteria.length > 0 ? (
                    <CheckboxGroup
                      options={options}
                      defaultSelected={styleCriteria.map(String)}
                      onSelect={ids => setSelection(ids.map(Number))}
                      disabledSearchList
                    />
                  ) : (
                    <p className={styles.emptyText}>No style criteria for this task type yet. Add one to start.</p>
                  )}
                  <FormError error={errors.styleCriteria?.message} />
                </div>
                <Button
                  type="button"
                  onClick={() => setShowFormModal(true)}
                  className={stepStyles.reportTypeButton}
                  view="secondary"
                >
                  <Icon
                    name="add"
                    style={{ fontSize: 22 }}
                    title="Add new style criterion"
                  />
                </Button>
              </div>
            )}
          </FormRow>
        </div>
      </ol>
      <Modal
        view="info"
        data={{ title: criterionToEdit ? 'Edit style criterion' : 'Add new style criterion' }}
        showModal={showFormModal}
        onClose={closeFormModal}
        className={stepStyles.modal}
      >
        <StyleCriterionForm
          taskType={taskType}
          criterion={criterionToEdit}
          closeModal={closeFormModal}
          onSaved={handleSaved}
        />
      </Modal>
      <Modal
        view="confirm"
        showModal={!!criterionToDelete}
        onClose={() => setCriterionToDelete(null)}
        data={{
          title: `Delete "${criterionToDelete?.name}"?`,
          text: 'The criterion is removed from every assessment that uses it. Past results keep a snapshot of the criterion as it was when they ran.',
          cancelButton: { text: 'Cancel', onClick: () => setCriterionToDelete(null) },
          confirmButton: { text: 'Delete', onClick: handleDelete },
        }}
      />
    </div>
  );
});
